import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Snackbar,
  Alert,
  Autocomplete,
  Chip,
  CircularProgress,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import { DataGrid } from '@mui/x-data-grid';
import axios from 'axios';
import { API_BASE_URL } from '../../../constants/constants';
import { jwtDecode } from 'jwt-decode';
import { LEVEL_NAMES } from '../../../constants/userLevels';
import { usePermissions } from '../../../context/PermissionContext';
import BackButton from '../../../components/BackButton';

const AssignPincodes = () => {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [addPincodeOpen, setAddPincodeOpen] = useState(false); // Dialog for adding pincode to DB
  const [selectedUser, setSelectedUser] = useState(null);
  const [availablePincodes, setAvailablePincodes] = useState([]);
  const [selectedPincodes, setSelectedPincodes] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [selectedDistricts, setSelectedDistricts] = useState([]); // Multi-select for districts
  const [taluks, setTaluks] = useState([]);
  const [selectedTaluks, setSelectedTaluks] = useState([]); // Multi-select for taluks
  const [newPincodes, setNewPincodes] = useState([]); // For new pincodes to be added to DB
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [userLevel, setUserLevel] = useState(null);
  const [userId, setUserId] = useState(null);
  const { can } = usePermissions();
  const [loadingDistricts, setLoadingDistricts] = useState(false);
  const [loadingTaluks, setLoadingTaluks] = useState(false);
  const [loadingPincodes, setLoadingPincodes] = useState(false);

  // Search & Level filter states
  const [dialogLevelFilter, setDialogLevelFilter] = useState('');
  const [tableLevelFilter, setTableLevelFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // View Pincodes Modal state for table rows
  const [viewPincodesModalOpen, setViewPincodesModalOpen] = useState(false);
  const [viewPincodesModalTitle, setViewPincodesModalTitle] = useState('');
  const [viewPincodesModalItems, setViewPincodesModalItems] = useState([]);

  const handleOpenViewPincodesModal = (title, items) => {
    setViewPincodesModalTitle(title);
    setViewPincodesModalItems(items || []);
    setViewPincodesModalOpen(true);
  };

  const levelNames = LEVEL_NAMES;

  useEffect(() => {
    const token = sessionStorage.getItem('token');
    if (token) {
      try {
        const decodedToken = jwtDecode(token);
        setUserLevel(decodedToken.level);
        setUserId(decodedToken.userId);
        fetchUsers(decodedToken.userId);
      } catch (error) {
        console.error('Error decoding token:', error);
        showSnackbar('Error with authentication. Please log in again.', 'error');
      }
    }
  }, []);

  const fetchUsers = async (userId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/users-with-pincodes/${userId}`);
      setUsers(response.data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      showSnackbar('Error fetching users', 'error');
    }
  };

  const fetchAvailablePincodes = async (userId) => {
    try {
      setLoadingPincodes(true);
      if (userLevel === 1) {
        const response = await axios.get(`${API_BASE_URL}/available-pincodes/${userId}`);
        setAvailablePincodes(response.data);
      } else {
        const response = await axios.get(`${API_BASE_URL}/privileges/${userId}`);
        const pincodes = response.data.pincodes || [];
        setAvailablePincodes(pincodes);
        if (pincodes.length === 0) {
          showSnackbar('No pincodes assigned to you', 'info');
        }
      }
    } catch (error) {
      console.error('Error fetching available pincodes:', error);
      showSnackbar('Error fetching available pincodes', 'error');
    } finally {
      setLoadingPincodes(false);
    }
  };

  const fetchDistricts = async () => {
    setLoadingDistricts(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/districts`);
      setDistricts(response.data);
    } catch (error) {
      console.error('Error fetching districts:', error);
      showSnackbar('Error fetching districts', 'error');
    } finally {
      setLoadingDistricts(false);
    }
  };

  const fetchTaluksForDistricts = async (districtIds) => {
    setLoadingTaluks(true);
    try {
      let allTaluks = [];
      for (const districtId of districtIds) {
        const response = await axios.get(`${API_BASE_URL}/taluks/district/${districtId}`);
        const taluksData = Array.isArray(response.data) ? response.data : [];
        allTaluks = [...allTaluks, ...taluksData];
      }
      const uniqueTaluks = allTaluks
        .filter((taluk, index, self) => index === self.findIndex((t) => t.name === taluk.name))
        .sort((a, b) => a.name.localeCompare(b.name));
      setTaluks(uniqueTaluks);
    } catch (error) {
      console.error('Error fetching taluks:', error);
      showSnackbar('Error fetching taluks', 'error');
    } finally {
      setLoadingTaluks(false);
    }
  };

  const fetchPincodesForTaluks = async (talukIds) => {
    setLoadingPincodes(true);
    try {
      const results = await Promise.allSettled(
        talukIds.map((id) => axios.get(`${API_BASE_URL}/pincodes/taluk/${id}`))
      );
      const allPincodes = results
        .filter((r) => r.status === 'fulfilled' && r.value && r.value.data && Array.isArray(r.value.data.pincodes))
        .flatMap((r) => r.value.data.pincodes);

      const uniqueSorted = [...new Set(allPincodes)].sort((a, b) => a - b);
      if (uniqueSorted.length === 0) {
        showSnackbar('No pincodes found for selected Constituencies', 'warning');
      }
      setAvailablePincodes(uniqueSorted);
    } catch (err) {
      console.error('Error fetching pincodes:', err);
      const errMsg = (err.response && err.response.data && err.response.data.message) ? err.response.data.message : err.message;
      showSnackbar('Error fetching pincodes: ' + errMsg, 'error');
    } finally {
      setLoadingPincodes(false);
    }
  };

  const handleOpenDialog = () => {
    if (!can('users.pincodes.assign')) {
      showSnackbar('You do not have permission to assign pincodes', 'error');
      return;
    }
    setOpen(true);
    setSelectedDistricts([]);
    setSelectedTaluks([]);
    setAvailablePincodes([]);
    setSelectedPincodes([]);
    
    if (userLevel === 1) {
      fetchDistricts();
    } else {
      fetchAvailablePincodes(userId);
    }
  };

  const handleCloseDialog = () => {
    setOpen(false);
    resetDialogState();
  };

  const resetDialogState = () => {
    setSelectedUser(null);
    setSelectedDistricts([]);
    setSelectedTaluks([]);
    setSelectedPincodes([]);
    setTaluks([]);
    setAvailablePincodes([]);
    setDialogLevelFilter('');
  };

  const handleUserChange = (event, value) => {
    setSelectedUser(value);
    setSelectedPincodes(value ? value.pincodes : []);
  };

  const handleDistrictChange = (event, newValue) => {
    const isSelectAll = newValue[newValue.length - 1] === 'all';
    if (isSelectAll) {
      setSelectedDistricts(districts.map(d => d._id));
    } else {
      setSelectedDistricts(newValue.map(d => d._id));
    }
    setSelectedTaluks([]);
    setAvailablePincodes([]);
    if (newValue.length > 0) {
      fetchTaluksForDistricts(isSelectAll ? districts.map(d => d._id) : newValue.map(d => d._id));
    }
  };

  const handleTalukChange = (event, newValue) => {
    const isSelectAll = newValue[newValue.length - 1] === 'all';
    if (isSelectAll) {
      setSelectedTaluks(taluks.map(t => t._id));
    } else {
      setSelectedTaluks(newValue.map(t => t._id));
    }
    setAvailablePincodes([]);
    if (newValue.length > 0) {
      fetchPincodesForTaluks(isSelectAll ? taluks.map(t => t._id) : newValue.map(t => t._id));
    }
  };

  const handlePincodeChange = (event, newValue) => {
    const isSelectAll = newValue[newValue.length - 1] === 'all';
    if (isSelectAll) {
      setSelectedPincodes(availablePincodes);
    } else {
      setSelectedPincodes(newValue);
    }
  };

  const handleAddNewPincodeChange = (event) => {
    if (event.key === 'Enter' && event.target.value.length === 6) {
      const value = event.target.value;
      if (/^\d{6}$/.test(value)) {
        setNewPincodes((prev) => [...prev, value]);
        event.target.value = '';
      } else {
        showSnackbar('Pincode must be 6 digits', 'error');
      }
    }
  };

  const handleDeleteNewPincode = (pincodeToDelete) => {
    setNewPincodes(newPincodes.filter((pincode) => pincode !== pincodeToDelete));
  };

  const handleSubmit = async () => {
    if (!can('users.pincodes.assign')) return;
    if (!selectedUser) {
      showSnackbar('Please select a user', 'error');
      return;
    }

    try {
      const { data } = await axios.get(`${API_BASE_URL}/privileges/${selectedUser.id}`);

      if (data && data.userId) {
        await axios.put(`${API_BASE_URL}/privileges/${selectedUser.id}`, {
          pincodes: selectedPincodes,
        });
        showSnackbar('Pincodes updated successfully', 'success');
      } else {
        await axios.put(`${API_BASE_URL}/privileges/${selectedUser.id}`, {
          userId: selectedUser.id,
          pincodes: selectedPincodes,
          modules: [],
        });
        showSnackbar('Privileges created successfully', 'success');
      }

      handleCloseDialog();
      fetchUsers(userId);
    } catch (error) {
      console.error('Error managing privileges:', error);
      const errMsg = (error.response && error.response.data && error.response.data.message) ? error.response.data.message : error.message;
      showSnackbar('Error managing privileges: ' + errMsg, 'error');
    }
  };

  const handleAddPincodeToDB = async () => {
    if (!can('users.pincodes.addToDb')) return;
    if (!selectedDistricts.length || !selectedTaluks.length || newPincodes.length === 0) {
      showSnackbar('Please select districts, Constituencies, and add pincodes', 'error');
      return;
    }

    try {
      await axios.post(`${API_BASE_URL}/add-pincode-to-db`, {
        districtIds: selectedDistricts,
        talukIds: selectedTaluks,
        pincodes: newPincodes,
      });

      showSnackbar('Pincodes added to database successfully', 'success');
      fetchPincodesForTaluks(selectedTaluks);
      setAddPincodeOpen(false);
    } catch (error) {
      console.error('Error adding pincodes to database:', error);
      showSnackbar('Error adding pincodes to database', 'error');
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const displayedUsers = users.filter((u) => {
    if (tableLevelFilter && String(u.level) !== String(tableLevelFilter)) {
      return false;
    }
    if (searchTerm) {
      const term = searchTerm.toLowerCase().trim();
      const nameMatch = u.fullName && u.fullName.toLowerCase().includes(term);
      const idMatch = (u.stateHeadId && String(u.stateHeadId).toLowerCase().includes(term)) || (u.id && String(u.id).toLowerCase().includes(term));
      const emailMatch = u.email && u.email.toLowerCase().includes(term);
      const mobileMatch = u.mobile && String(u.mobile).includes(term);
      const pincodeMatch = u.pincodes && u.pincodes.some((p) => String(p).includes(term));
      const levelMatch = (u.levelName && u.levelName.toLowerCase().includes(term)) || String(u.level).includes(term);
      return nameMatch || idMatch || emailMatch || mobileMatch || pincodeMatch || levelMatch;
    }
    return true;
  });

  const filteredUsersForDialog = users.filter((u) => {
    if (dialogLevelFilter && String(u.level) !== String(dialogLevelFilter)) {
      return false;
    }
    return true;
  });

  const getDisplayId = (user) => {
    if (!user) return 'N/A';
    const val = user.stateHeadId || user.displayId || user.usercode || user.msbId || user.agentId || user.hoId;
    if (val && typeof val === 'string' && val.length < 24) {
      return val;
    }
    const idStr = user.id || user._id ? String(user.id || user._id) : '';
    const hex = idStr.slice(-6).toUpperCase();
    if (user.level === 2) return `QPAY-${hex}`;
    if (user.level === 3) return `ST-${hex}`;
    if (user.level === 4) return `CTR-${hex}`;
    if (user.level === 5) return `AGT-${hex}`;
    return hex ? `USR-${hex}` : 'N/A';
  };

  const columns = [
    {
      field: 'sl',
      headerName: 'SL',
      width: 70,
      sortable: false,
      renderCell: (params) => {
        const index = displayedUsers.findIndex((r) => r.id === params.row.id);
        return (
          <Typography variant="body2" sx={{ fontWeight: 700, color: '#64748b' }}>
            {index >= 0 ? index + 1 : ''}
          </Typography>
        );
      },
    },
    {
      field: 'stateHeadId',
      headerName: 'ID',
      width: 140,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 600, color: '#0f172a' }}>
          {getDisplayId(params.row)}
        </Typography>
      ),
    },
    { field: 'fullName', headerName: 'Full Name', width: 180 },
    { field: 'mobile', headerName: 'Mobile', width: 140 },
    { field: 'email', headerName: 'Email', width: 180 },
    {
      field: 'levelName',
      headerName: 'User Type',
      width: 160,
      renderCell: (params) => {
        const lvl = levelNames[params.row.level] || params.row.levelName || (params.row.level ? `Level ${params.row.level}` : 'N/A');
        return <Chip label={lvl} color="primary" size="small" variant="outlined" sx={{ fontWeight: 700 }} />;
      },
    },
    {
      field: 'pincodes',
      headerName: 'Pincodes',
      flex: 1,
      minWidth: 280,
      sortable: false,
      renderCell: (params) => {
        const pincodesList = params.value || [];
        if (!pincodesList.length) {
          return (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              None assigned
            </Typography>
          );
        }

        const maxVisible = 3;
        const visiblePincodes = pincodesList.slice(0, maxVisible);
        const overflowCount = pincodesList.length - maxVisible;

        return (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 0.5,
              alignItems: 'center',
              py: 0.5,
              width: '100%',
            }}
          >
            {visiblePincodes.map((pin, idx) => (
              <Chip
                key={idx}
                label={pin}
                size="small"
                color="primary"
                variant="outlined"
                sx={{ fontWeight: 600, fontSize: '0.75rem' }}
              />
            ))}
            {overflowCount > 0 && (
              <Chip
                label={`+${overflowCount} more`}
                size="small"
                color="secondary"
                onClick={() =>
                  handleOpenViewPincodesModal(
                    `Pincodes for ${params.row.fullName || 'User'}`,
                    pincodesList
                  )
                }
                sx={{ fontWeight: 700, cursor: 'pointer' }}
              />
            )}
          </Box>
        );
      },
    },
  ];

  return (
    <Box sx={{ p: 3, pr: 4, mb: 4, width: '100%', boxSizing: 'border-box' }}>
      <BackButton />
      <br />
      <br />
      <Paper elevation={0} sx={{ p: 3.5, pr: 4, borderRadius: 3, border: '1px solid #e2e8f0', background: '#ffffff', boxSizing: 'border-box' }}>
        {/* Page Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, pr: 1 }}>
          <Box>
            <Typography variant="h5" color="primary" sx={{ fontWeight: 800, mb: 0.5 }}>
              Assign Pincodes
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Assign and manage pincode coverage and geographic privileges across all administrators
            </Typography>
          </Box>

          {can('users.pincodes.assign') && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleOpenDialog}
              sx={{ fontWeight: 700 }}
            >
              Assign Pincodes
            </Button>
          )}
        </Box>

        {/* Filter Section */}
        <Paper elevation={0} sx={{ p: 2.5, px: 3, mb: 3, mt: 2, borderRadius: 2, border: '1px solid #e2e8f0', backgroundColor: '#f8fafc' }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={7}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search Name, ID, Email, Mobile or Pincode..."
                variant="outlined"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={5}>
              <FormControl fullWidth size="small">
                <InputLabel>User Type</InputLabel>
                <Select
                  value={tableLevelFilter}
                  label="User Type"
                  onChange={(e) => setTableLevelFilter(e.target.value)}
                >
                  <MenuItem value="">All Types</MenuItem>
                  <MenuItem value="2">{LEVEL_NAMES[2]}</MenuItem>
                  <MenuItem value="3">{LEVEL_NAMES[3]}</MenuItem>
                  <MenuItem value="4">{LEVEL_NAMES[4]}</MenuItem>
                  <MenuItem value="5">{LEVEL_NAMES[5]}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>

        <Box sx={{ height: 550, width: '100%', overflow: 'auto' }}>
          <DataGrid
            rows={displayedUsers}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[10, 20, 50]}
            disableSelectionOnClick
            getRowHeight={() => 'auto'}
            sx={{
              border: 'none',
              width: '100%',
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: '#f8fafc',
                fontWeight: 700,
                color: '#1e293b',
                fontSize: '0.85rem',
                px: 1,
              },
              '& .MuiDataGrid-cell': {
                py: 1.5,
                px: 1.5,
                display: 'flex',
                alignItems: 'center',
                whiteSpace: 'nowrap',
              },
              '& .MuiDataGrid-row:hover': {
                backgroundColor: '#f8fafc',
              },
              '& .MuiDataGrid-footerContainer': {
                borderTop: '1px solid #e2e8f0',
                px: 2,
              },
            }}
          />
        </Box>
      </Paper>

        {/* Dialog for Assigning Pincodes */}
        <Dialog open={open} onClose={handleCloseDialog} fullWidth maxWidth="sm">
          <DialogTitle sx={{ fontWeight: 700 }}>Assign Pincodes</DialogTitle>
          <DialogContent>
            <FormControl fullWidth sx={{ mb: 2, mt: 1 }}>
              <InputLabel>User Type</InputLabel>
              <Select
                value={dialogLevelFilter}
                label="User Type"
                onChange={(e) => {
                  setDialogLevelFilter(e.target.value);
                  setSelectedUser(null);
                  setSelectedPincodes([]);
                }}
              >
                <MenuItem value="">All Types</MenuItem>
                <MenuItem value="2">{LEVEL_NAMES[2]}</MenuItem>
                <MenuItem value="3">{LEVEL_NAMES[3]}</MenuItem>
                <MenuItem value="4">{LEVEL_NAMES[4]}</MenuItem>
                <MenuItem value="5">{LEVEL_NAMES[5]}</MenuItem>
              </Select>
            </FormControl>

            <Autocomplete
              options={filteredUsersForDialog}
              getOptionLabel={(option) => {
                const lvl = levelNames[option.level] || option.levelName || `Level ${option.level}`;
                const idVal = getDisplayId(option);
                return `${option.fullName} (ID: ${idVal} | ${lvl} | Mobile: ${option.mobile || ''} | Email: ${option.email || ''})`;
              }}
              value={selectedUser}
              onChange={handleUserChange}
              filterOptions={(options, state) => {
                const input = state.inputValue.toLowerCase().trim();
                if (!input) return options;
                return options.filter((opt) => {
                  const idVal = getDisplayId(opt);
                  const nameMatch = opt.fullName && opt.fullName.toLowerCase().includes(input);
                  const idMatch = idVal.toLowerCase().includes(input) || (opt.id && String(opt.id).toLowerCase().includes(input));
                  const mobileMatch = opt.mobile && String(opt.mobile).includes(input);
                  const emailMatch = opt.email && opt.email.toLowerCase().includes(input);
                  const levelMatch = (levelNames[opt.level] && levelNames[opt.level].toLowerCase().includes(input)) || (opt.levelName && opt.levelName.toLowerCase().includes(input)) || String(opt.level).includes(input);
                  return nameMatch || idMatch || mobileMatch || emailMatch || levelMatch;
                });
              }}
              renderOption={(props, option) => {
                const lvl = levelNames[option.level] || option.levelName || `Level ${option.level}`;
                const idVal = getDisplayId(option);
                return (
                  <Box component="li" {...props} key={option.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', py: 1, borderBottom: '1px solid #f1f5f9' }}>
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 600, color: '#0f172a' }}>
                        {option.fullName} <Typography component="span" variant="caption" sx={{ color: '#64748b', ml: 1 }}>ID: {idVal}</Typography>
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        Mobile: {option.mobile || 'N/A'} | Email: {option.email || 'N/A'}
                      </Typography>
                    </Box>
                    <Chip label={lvl} size="small" color="primary" variant="outlined" sx={{ fontWeight: 700 }} />
                  </Box>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select User (Search Name, ID, Mobile, Email)"
                  variant="outlined"
                  placeholder="Search user by Name, ID, Email, or Mobile..."
                />
              )}
              sx={{ mb: 2 }}
            />

            {userLevel <= 1 && (
              <React.Fragment>
                <Autocomplete
                  multiple
                  options={['all', ...districts]}
                  getOptionLabel={(option) => option === 'all' ? 'Select All' : option.name}
                  value={districts.filter(d => selectedDistricts.includes(d._id))}
                  onChange={handleDistrictChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Districts"
                      variant="outlined"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <React.Fragment>
                            {loadingDistricts && <CircularProgress color="inherit" size={20} />}
                            {params.InputProps.endAdornment}
                          </React.Fragment>
                        ),
                      }}
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value
                      .filter(v => v !== 'all')
                      .map((option, index) => (
                        <Chip
                          key={option._id}
                          label={option.name}
                          {...getTagProps({ index })}
                        />
                      ))
                  }
                  sx={{ mb: 2 }}
                />
                <Autocomplete
                  multiple
                  options={['all', ...taluks]}
                  getOptionLabel={(option) => option === 'all' ? 'Select All' : option.name}
                  value={taluks.filter(t => selectedTaluks.includes(t._id))}
                  onChange={handleTalukChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Constituencies"
                      variant="outlined"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <React.Fragment>
                            {loadingTaluks && <CircularProgress color="inherit" size={20} />}
                            {params.InputProps.endAdornment}
                          </React.Fragment>
                        ),
                      }}
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value
                      .filter(v => v !== 'all')
                      .map((option, index) => (
                        <Chip
                          key={option._id}
                          label={option.name}
                          {...getTagProps({ index })}
                        />
                      ))
                  }
                  sx={{ mb: 2 }}
                />
                {can('users.pincodes.addToDb') && (
                  <Button onClick={() => setAddPincodeOpen(true)} variant="outlined" sx={{ mb: 2 }}>
                    Add Pincode to DB
                  </Button>
                )}
              </React.Fragment>
            )}
            <Autocomplete
              multiple
              options={['all', ...availablePincodes]}
              getOptionLabel={(option) => option === 'all' ? 'Select All' : String(option)}
              value={selectedPincodes}
              onChange={handlePincodeChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Pincodes"
                  variant="outlined"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <React.Fragment>
                        {loadingPincodes && <CircularProgress color="inherit" size={20} />}
                        {params.InputProps.endAdornment}
                      </React.Fragment>
                    ),
                  }}
                />
              )}
              renderTags={(value, getTagProps) =>
                value
                  .filter(v => v !== 'all')
                  .map((option, index) => (
                    <Chip
                      key={option}
                      label={option}
                      {...getTagProps({ index })}
                    />
                  ))
              }
              sx={{ mb: 2 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} color="secondary">
              Cancel
            </Button>
            <Button onClick={handleSubmit} color="primary" variant="contained">
              Submit
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog for Adding Pincode to DB */}
        <Dialog open={addPincodeOpen} onClose={() => setAddPincodeOpen(false)} fullWidth maxWidth="sm">
          <DialogTitle sx={{ fontWeight: 700 }}>Add Pincode to Database</DialogTitle>
          <DialogContent>
            <Autocomplete
              multiple
              options={['all', ...districts]}
              getOptionLabel={(option) => option === 'all' ? 'Select All' : option.name}
              value={districts.filter(d => selectedDistricts.includes(d._id))}
              onChange={handleDistrictChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Districts"
                  variant="outlined"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <React.Fragment>
                        {loadingDistricts && <CircularProgress color="inherit" size={20} />}
                        {params.InputProps.endAdornment}
                      </React.Fragment>
                    ),
                  }}
                />
              )}
              renderTags={(value, getTagProps) =>
                value
                  .filter(v => v !== 'all')
                  .map((option, index) => (
                    <Chip
                      key={option._id}
                      label={option.name}
                      {...getTagProps({ index })}
                    />
                  ))
              }
              sx={{ mb: 2, mt: 1 }}
            />
            <Autocomplete
              multiple
              options={['all', ...taluks]}
              getOptionLabel={(option) => option === 'all' ? 'Select All' : option.name}
              value={taluks.filter(t => selectedTaluks.includes(t._id))}
              onChange={handleTalukChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Constituencies"
                  variant="outlined"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <React.Fragment>
                        {loadingTaluks && <CircularProgress color="inherit" size={20} />}
                        {params.InputProps.endAdornment}
                      </React.Fragment>
                    ),
                  }}
                />
              )}
              renderTags={(value, getTagProps) =>
                value
                  .filter(v => v !== 'all')
                  .map((option, index) => (
                    <Chip
                      key={option._id}
                      label={option.name}
                      {...getTagProps({ index })}
                    />
                  ))
              }
              sx={{ mb: 2 }}
            />
            <TextField
              label="Add New Pincode"
              variant="outlined"
              fullWidth
              onKeyDown={handleAddNewPincodeChange}
              placeholder="Enter 6-digit pincode and press Enter"
              sx={{ mb: 2 }}
            />
            <Box sx={{ mb: 2 }}>
              {newPincodes.map((pincode, index) => (
                <Chip
                  key={index}
                  label={pincode}
                  onDelete={() => handleDeleteNewPincode(pincode)}
                  sx={{ mr: 1, mb: 1 }}
                />
              ))}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAddPincodeOpen(false)} color="secondary">
              Cancel
            </Button>
            <Button onClick={handleAddPincodeToDB} color="primary" variant="contained">
              Add to DB
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog for Viewing All Assigned Pincodes */}
        <Dialog
          open={viewPincodesModalOpen}
          onClose={() => setViewPincodesModalOpen(false)}
          fullWidth
          maxWidth="xs"
        >
          <DialogTitle sx={{ fontWeight: 700, color: '#0f172a' }}>
            {viewPincodesModalTitle}
          </DialogTitle>
          <DialogContent dividers>
            {viewPincodesModalItems.length > 0 ? (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, py: 1 }}>
                {viewPincodesModalItems.map((pin, idx) => (
                  <Chip key={idx} label={pin} color="primary" variant="filled" sx={{ fontWeight: 700 }} />
                ))}
              </Box>
            ) : (
              <Typography color="text.secondary" align="center">
                No pincodes found
              </Typography>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setViewPincodesModalOpen(false)} color="inherit" variant="outlined">
              Close
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={() => setSnackbarOpen(false)}
            severity={snackbarSeverity}
            sx={{ width: '100%' }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
    </Box>
  );
};

export default AssignPincodes;